/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { CheckmarkTiny } from "@duxton-web/icons"
import { Button, Space, Timeline, Typography } from "@duxton-web/ui"
import classNames from "classnames"

import {
  RETURN_CONTEXTUAL_ACTION_LABEL,
  RETURN_TIMELINE_ATTRIBUTION_LABEL,
  RETURN_TIMELINE_COUNT_LABEL,
  RETURN_TIMELINE_LABEL,
  RETURN_TIMELINE_STATE,
  ReturnContextualAction,
  ReturnTimelineEntry,
  ReturnTimelineState,
} from "ecommerce/constants/returnRefund"
import {
  toMexTrackingDeeplink,
  toTrackingHref,
} from "ecommerce/utils/returnRefundHelpers"
import { isWebview } from "utils/webview"

import styles from "./ReturnStatusTimeline.modules.css"

const { Text } = Typography

const STATE_CLASS: Record<ReturnTimelineState, string> = {
  [RETURN_TIMELINE_STATE.complete]: styles.complete,
  [RETURN_TIMELINE_STATE.current]: styles.current,
  [RETURN_TIMELINE_STATE.upcoming]: styles.upcoming,
}

interface StepDotProps {
  state: ReturnTimelineState
}

const StepDot: FC<StepDotProps> = ({ state }) => (
  <span className={classNames(styles.dot, STATE_CLASS[state])}>
    {state === RETURN_TIMELINE_STATE.complete && (
      <CheckmarkTiny className={styles.dotIcon} />
    )}
  </span>
)

interface ReturnStatusTimelineProps {
  /**
   * Steps to render. Step count and labels vary by outcome — three for a closed
   * auto-refund, five for an open request — so nothing here assumes a fixed set.
   */
  entries: ReturnTimelineEntry[]
  /**
   * The FALLBACK hand-off only. An action whose destination resolves to a link
   * (`Track here` with BE's tracking deeplink) renders as a real anchor and never
   * calls this — it fires solely for actions without a resolvable destination
   * (`Contact Driver`, `Contact Grab support`, or a malformed deeplink), with the
   * raw wire value as `href`. Side effects that must run on EVERY action click
   * (analytics, mark-as-viewed) do not belong here.
   */
  onAction(action: ReturnContextualAction, href?: string): void
}

export const ReturnStatusTimeline: FC<ReturnStatusTimelineProps> = ({
  entries,
  onAction,
}) => (
  <Timeline
    className={styles.timeline}
    items={entries.map((entry, index) => {
      // Falsy guard on purpose: a mechanical `count: 0` must not render `0 Rejected`.
      const countLabel = entry.count
        ? RETURN_TIMELINE_COUNT_LABEL[entry.step]?.(entry.count)
        : undefined
      // A const so the narrowing survives into the onClick closure — and a step
      // can legitimately appear twice (e.g. a retried delivery), hence the
      // index-composite key.
      const action = entry.action
      /*
       * BE sends the CONSUMER app's deeplink
       * (`grab://open?screenType=EXPRESS_WEB_TRACKING&page=<https URL>`), which
       * neither host can open verbatim. The webview re-wraps it for the merchant
       * app's own scheme (`grabmex://EXPRESS?url=…`) so the app's URL interception
       * routes it; every other host extracts the tracking page and opens it as an
       * ordinary web page. Unresolvable values fall back to the `onAction` button.
       */
      const inApp = isWebview()
      const actionHref = inApp
        ? toMexTrackingDeeplink(entry.actionHref)
        : toTrackingHref(entry.actionHref)

      return {
        key: `${entry.step}-${index}`,
        className: STATE_CLASS[entry.state],
        dot: <StepDot state={entry.state} />,
        children: (
          <Space direction="vertical" size={2}>
            <div className={styles.labelRow}>
              <Text strong className={styles.label}>
                {/* The server renders the timeline copy (the contract forbids deriving
                    it client-side), so its title wins; the label map serves mock-backed
                    entries, and the raw code is the last resort. */}
                {entry.title ?? RETURN_TIMELINE_LABEL[entry.step] ?? entry.step}
              </Text>
              {countLabel && (
                <Text type="secondary" strong className={styles.small}>
                  {countLabel}
                </Text>
              )}
            </div>
            {entry.detail && (
              <Text type="secondary" className={styles.small}>
                {entry.detail}
              </Text>
            )}
            {entry.attribution && (
              <Text type="secondary" className={styles.small}>
                {RETURN_TIMELINE_ATTRIBUTION_LABEL[entry.attribution]}
              </Text>
            )}
            {action && actionHref && inApp && (
              /*
               * A real anchor, not an onClick: WKWebView reports a link click to
               * the app as `.linkActivated` and a scripted `location.href` as
               * `.other`, and the app's URL interception keys on the former. No
               * `target` — a scheme must stay in this document, or the browser
               * strands an empty tab on the deeplink URL.
               */
              <Button
                type="link"
                className={styles.actionLink}
                href={actionHref}
              >
                {entry.actionLabel ?? RETURN_CONTEXTUAL_ACTION_LABEL[action]}
              </Button>
            )}
            {action && actionHref && !inApp && (
              /*
               * The extracted page as an ordinary navigation — so this one DOES
               * take a new tab, otherwise the merchant loses the return request
               * they were reading.
               */
              <Button
                type="link"
                className={styles.actionLink}
                href={actionHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {entry.actionLabel ?? RETURN_CONTEXTUAL_ACTION_LABEL[action]}
              </Button>
            )}
            {action && !actionHref && (
              // A real button so the hand-off is focusable and keyboard-operable —
              // a Link without href never enters the tab order.
              <Button
                type="link"
                className={styles.actionLink}
                onClick={() => onAction(action, entry.actionHref)}
              >
                {entry.actionLabel ?? RETURN_CONTEXTUAL_ACTION_LABEL[action]}
              </Button>
            )}
          </Space>
        ),
      }
    })}
  />
)
